import api from './api';

/**
 * Poll scheduling (投票型日程調整) API client.
 */

export type PollDenialReason =
  | 'globally_disabled'
  | 'account_disabled'
  | 'plan_excluded'
  | 'no_organization'
  | 'error';

export type PollResponseMode = 'yes_maybe_no' | 'yes_no';
export type PollVisibility = 'link' | 'invite_only';
export type PollStatus = 'open' | 'confirmed' | 'closed' | 'expired';
export type PollAnswer = 'yes' | 'maybe' | 'no';

export interface PollAvailability {
  enabled: boolean;
  reason: PollDenialReason | null;
}

export interface PollOption {
  id: number;
  startTime: string;
  endTime: string;
  sortOrder: number;
  /** Non-null when a respondent proposed this slot rather than the host. */
  suggestedByParticipantId?: number | null;
}

export interface PollParticipant {
  id: number;
  name: string;
  /** Host-only. Absent when a respondent loads the poll. */
  email?: string | null;
  invitedAt?: string | null;
  respondedAt?: string | null;
  remindedAt?: string | null;
  /** optionId → answer */
  answers: Record<number, PollAnswer>;
}

export interface PollTallyRow {
  optionId: number;
  yes: number;
  maybe: number;
  no: number;
}

export interface PollDetail {
  id: number;
  publicId: string;
  title: string;
  description?: string | null;
  durationMinutes: number;
  timeZone: string;
  responseMode: PollResponseMode;
  visibility: PollVisibility;
  hideResponsesFromParticipants: boolean;
  /** Respondents may propose slots the host did not offer. */
  allowSuggestions: boolean;
  /**
   * Someone answering must give an email address. Invite-only polls require one
   * regardless — use `requireEmail || visibility === 'invite_only'`.
   */
  requireEmail: boolean;
  /** Grid rows read "09:00–10:00" instead of "09:00". */
  showEndTime: boolean;
  /**
   * An account stands behind this poll. False for one made with the free
   * tool, which collects no email address because there is nobody to send
   * one to.
   */
  hasOwner?: boolean;
  status: PollStatus;
  confirmedOptionId?: number | null;
  createdAt: string;
  options: PollOption[];
  participants: PollParticipant[];
  tally: PollTallyRow[];
  respondedCount: number;
  pendingCount: number;
  /**
   * How many people may answer, from the owner's plan. Host view only.
   * Equal to MAX_PARTICIPANTS when the plan sets no limit.
   */
  participantLimit?: number;
  /**
   * Host branding for the answering page, from the poll's meeting type.
   * Null on anonymous polls — no account, so no brand, and the SailLab mark
   * shows (nobody has paid to remove it).
   */
  branding?: {
    coverPhotoUrl: string | null;
    coverPhotoUrlMobile: string | null;
    /**
     * The host both asked to remove the SailLab mark AND has the plan feature
     * for it. Resolved server-side — never re-derive it from a user flag here.
     */
    hideSailLabBranding: boolean;
  } | null;
  /** Public view only — which participant row belongs to the current viewer. */
  viewerParticipantId?: number | null;
  /** Returned once after answering, so the respondent can edit their own row. */
  editToken?: string;
  /**
   * The caller proved possession of the manage token, so this is the anonymous
   * creator of a poll that has no account. They get the organizer's view.
   */
  isManager?: boolean;
  /**
   * The manage secret for a poll created with no account. Returned ONCE, by the
   * create call, and by nothing else ever again — keep it or the poll has no
   * organizer.
   */
  adminToken?: string;
}

export interface PollSummary {
  id: number;
  publicId: string;
  title: string;
  status: PollStatus;
  visibility: PollVisibility;
  durationMinutes: number;
  timeZone: string;
  optionCount: number;
  createdAt: string;
}

export interface CreatePollPayload {
  title: string;
  description?: string;
  durationMinutes: number;
  timeZone: string;
  responseMode: PollResponseMode;
  visibility: PollVisibility;
  hideResponsesFromParticipants: boolean;
  allowSuggestions: boolean;
  requireEmail: boolean;
  showEndTime: boolean;
  /** One of COVER_TEMPLATES' imageUrl values. Omitted = no cover. */
  coverPhotoUrl?: string;
  /**
   * The host picks a date range and a working window; the server expands them
   * into slots. Sent instead of an explicit option list so the rules about
   * window length, slot fit and the maximum are enforced in one place.
   */
  slotRange: {
    startDate: string;
    endDate: string;
    startTime: string;
    endTime: string;
    weekdays?: number[];
  };
}

/**
 * Whether this organization may use polls.
 *
 * Deliberately NOT `checkFeature('meeting-polls')`: that endpoint only knows
 * about plan features and is blind to the global kill switch and the
 * per-account override, so using it would show the poll option to accounts the
 * API then refuses.
 *
 * Fails closed — any error resolves to disabled, matching the server.
 */
export const getPollAvailability = async (): Promise<PollAvailability> => {
  try {
    const data = await api.get('/polls/availability');
    return { enabled: data?.enabled === true, reason: data?.reason ?? null };
  } catch (error) {
    return { enabled: false, reason: 'error' };
  }
};

export const createPoll = async (payload: CreatePollPayload): Promise<PollDetail> => {
  return api.post('/polls', payload);
};

export const listPolls = async (): Promise<{ polls: PollSummary[]; total: number }> => {
  return api.get('/polls');
};

export const getPoll = async (pollId: number): Promise<PollDetail> => {
  return api.get(`/polls/${pollId}`);
};

/**
 * The same poll, addressed by its meeting-type row.
 * The meeting-type list knows meeting-type ids, not poll ids.
 */
export const getPollByMeetingType = async (meetingTypeId: number): Promise<PollDetail> => {
  return api.get(`/polls/meeting-type/${meetingTypeId}`);
};

export const updatePoll = async (
  pollId: number,
  updates: Partial<
    Pick<
      PollDetail,
      | 'publicId'
      | 'title'
      | 'description'
      | 'hideResponsesFromParticipants'
      | 'allowSuggestions'
      | 'requireEmail'
      | 'showEndTime'
      | 'visibility'
    >
  >
): Promise<PollDetail> => {
  return api.patch(`/polls/${pollId}`, updates);
};

export interface ReplaceOptionsResult extends PollDetail {
  added: number;
  removed: number;
  /** Answers lost because their slot was removed. Show this to the host. */
  discardedAnswers: number;
}

/**
 * Replace the candidate slots, given the RANGE they should come from.
 *
 * The range is expanded into slots **server-side**, in the poll's own time
 * zone. The client deliberately does not do this: it used to, with its own copy
 * of the expansion loop, and because that loop resolved wall-clock times in the
 * browser's zone while the server resolved them in its own (`TZ=UTC`), the same
 * range meant different times depending on which screen you saved from.
 *
 * Slots are matched by time, so untouched ones keep their votes; only removed
 * ones lose answers, and the count comes back so the host can be told.
 */
export const replacePollOptions = async (
  pollId: number,
  slotRange: {
    startDate: string;
    endDate: string;
    startTime: string;
    endTime: string;
    weekdays?: number[];
  }
): Promise<ReplaceOptionsResult> => {
  return api.put(`/polls/${pollId}/options`, { slotRange });
};

export interface InviteResult extends PollDetail {
  invited: number;
  /** Already on the poll, so not invited twice. */
  skipped: number;
}

/** Invite people by email. Each gets a personal link carrying their token. */
export const invitePeople = async (pollId: number, emails: string[]): Promise<InviteResult> => {
  return api.post(`/polls/${pollId}/invitations`, { emails });
};

export const confirmPoll = async (pollId: number, optionId: number): Promise<PollDetail> => {
  return api.post(`/polls/${pollId}/confirm`, { optionId });
};

export const reopenPoll = async (pollId: number): Promise<PollDetail> => {
  return api.post(`/polls/${pollId}/reopen`, {});
};

export const closePoll = async (pollId: number): Promise<PollDetail> => {
  return api.post(`/polls/${pollId}/close`, {});
};

export const deletePoll = async (pollId: number): Promise<{ success: boolean }> => {
  return api.delete(`/polls/${pollId}`);
};

// ─── Public (answering) ─────────────────────────────────────────────────
// These are used by people with no account — the host's guests.

export const getPublicPoll = async (
  publicId: string,
  inviteToken?: string | null,
  manageToken?: string | null
): Promise<PollDetail> => {
  const params = new URLSearchParams();
  if (inviteToken) params.set('invite', inviteToken);
  // Identifies the anonymous creator, who gets the organizer's view of their
  // own poll. Ignored — not refused — when it does not match.
  if (manageToken) params.set('manage', manageToken);
  const query = params.toString();
  return api.get(`/polls/public/${publicId}${query ? `?${query}` : ''}`);
};

export interface PublicPollLimits {
  /** People who may answer a poll that belongs to no account. */
  anonymous: number;
  /** The Free plan's limit. `null` = the plan sets none. */
  free: number | null;
  /** Absolute ceiling on any one poll, whatever the plan says. */
  max: number;
}

/**
 * The participant limits, as configured — never hardcode these.
 *
 * Both numbers are admin-editable (the account-less cap in system settings, the
 * Free plan's in the plan row), so a screen that prints its own copy of them
 * starts lying the day someone changes one. Public, cached 60s server-side.
 */
export const getPublicPollLimits = async (): Promise<PublicPollLimits> => {
  return api.get('/polls/public/limits');
};

/**
 * Create a poll with no account at all — the free tool.
 *
 * Takes the same payload as `createPoll`; the server ignores and overrides the
 * privacy fields, because a poll with no owner has nobody to keep those
 * promises for. The response carries `adminToken` exactly once.
 */
export const createPublicPoll = async (payload: CreatePollPayload): Promise<PollDetail> => {
  return api.post('/polls/public', payload);
};

/**
 * Move an anonymous poll into the signed-in user's account.
 *
 * A move, not a copy: the poll keeps its publicId, so links already shared keep
 * working and answers already given come with it. The manage token is the proof
 * that the caller is the poll's creator rather than someone who was sent the
 * share link.
 */
export const claimPoll = async (
  publicId: string,
  adminToken: string
): Promise<PollDetail & { pollId: number }> => {
  return api.post(`/polls/public/${publicId}/claim`, { adminToken });
};

export interface SubmitAnswersPayload {
  name: string;
  email?: string;
  marketingConsent?: boolean;
  /** Ticked the terms box. Only ever offered on a public poll. */
  acceptedTerms?: boolean;
  answers: Record<number, PollAnswer>;
  /**
   * Slots this person proposed, each carrying the mark they put on it —
   * proposing a time and voting on it are one act in the grid. Ignored unless
   * the poll allows suggestions.
   */
  suggestedSlots?: Array<{ startTime: string; endTime: string; answer: PollAnswer }>;
  inviteToken?: string | null;
  editToken?: string | null;
}

export const submitAnswers = async (
  publicId: string,
  payload: SubmitAnswersPayload
): Promise<PollDetail> => {
  return api.post(`/polls/public/${publicId}/answers`, payload);
};

export default {
  getPollAvailability,
  createPoll,
  listPolls,
  getPoll,
  getPollByMeetingType,
  updatePoll,
  replacePollOptions,
  invitePeople,
  confirmPoll,
  reopenPoll,
  closePoll,
  deletePoll,
  getPublicPoll,
  getPublicPollLimits,
  createPublicPoll,
  claimPoll,
  submitAnswers,
};
